<template>
	<div class="layers-details-panel">
		<div class="top-section">
			<div class="header">
				<h2 class="title">{{ t("layer-details.title") }}</h2>
				<div class="header-actions">
					<button
						type="button"
						class="icon-button collapse-btn"
						:title="$t('callToActions.collapsePanel')"
						:aria-label="$t('callToActions.collapsePanel')"
						@click="emit('collapse')"
					>
						<AppIcon :name="IconName.PanelRightCollapse" :size="18" :stroke-width="1.2" />
					</button>
					<button
						type="button"
						class="icon-button collapse-btn"
						:title="$t('callToActions.close')"
						:aria-label="$t('callToActions.close')"
						@click="emit('close')"
					>
						<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
					</button>
				</div>
			</div>

			<div class="separator"></div>
		</div>

		<div class="scroll-body">
			<div class="accordions">
				<AccordionRoot
					:key="accordionKey"
					:multiple="true"
					:collapsible="true"
					:default-value="defaultOpen"
					:persistence-key="persistenceKey"
				>
					<AccordionItem
						v-for="section in orderedSections"
						:key="section.type"
						:value="section.type"
						:title="typeTitle(section.type)"
						:active-count="section.items.length"
					>
						<div class="layer-cards">
							<div
								v-for="item in section.items"
								:key="item.id"
								:class="['layer-card', { 'city-pv-card': item.type === LayerFeatureType.CityPvDocument }]"
							>
								<div v-if="nameValue(item) || sourceValue(item) || badgeRows(item).length" class="card-header">
									<span v-if="nameValue(item)" class="card-title">
										<span v-if="transitLineDotClass(item)" :class="['line-dot', transitLineDotClass(item)]" />
										{{ nameValue(item) }}
									</span>
									<div v-if="sourceValue(item) || badgeRows(item).length" class="card-badges">
										<span v-if="sourceValue(item)" :class="['badge', sourceClass(item)]" :style="sourceStyle(item)">{{
											sourceValue(item)
										}}</span>
										<span v-for="row in badgeRows(item)" :key="row.label" :class="['badge', row.badgeClass]">{{
											row.text
										}}</span>
									</div>
								</div>
								<div v-if="hasDetailRows(item)" class="detail-rows">
									<template v-if="item.type === LayerFeatureType.CityPvDocument">
										<div
											v-for="row in cityPvRows(item)"
											:key="row.label"
											:class="['detail-row', { 'detail-row-stacked': row.label === 'full-minutes' }]"
										>
											<span class="detail-label">{{ fieldLabel(row.label) }}</span>
											<a v-if="row.isLink" :href="row.value" target="_blank" rel="noopener" class="link-pill">
												{{ t("opportunities.link_cta") }}
												<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
											</a>
											<div v-else-if="row.label === 'full-minutes'" class="minutes-value">
												<p :class="{ 'is-expanded': isCityPvMinutesExpanded(item.id) }">{{ row.value }}</p>
												<button
													v-if="shouldCollapseCityPvMinutes(row.value)"
													type="button"
													class="minutes-toggle"
													:aria-expanded="isCityPvMinutesExpanded(item.id)"
													@click="toggleCityPvMinutes(item.id)"
												>
													{{
														isCityPvMinutesExpanded(item.id)
															? t("layer-details.show-less")
															: t("layer-details.show-more")
													}}
												</button>
											</div>
											<span v-else class="detail-value">{{ row.value }}</span>
										</div>
									</template>
									<template v-else>
										<div v-for="row in detailRows(item)" :key="row.label" class="detail-row">
											<span class="detail-label">{{ fieldLabel(row.label) }}</span>
											<span class="detail-value">
												<span v-if="row.dotClass" :class="['value-dot', row.dotClass]" />
												{{ row.text }}
											</span>
										</div>
										<div v-for="(row, i) in linkSpecs(item)" :key="`link-${i}`" class="detail-row">
											<span class="detail-label">{{ fieldLabel(row.label) }}</span>
											<a :href="row.value!" target="_blank" rel="noopener" class="link-pill">
												{{ t("opportunities.link_cta") }}
												<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
											</a>
										</div>
									</template>
								</div>
							</div>
						</div>
					</AccordionItem>
				</AccordionRoot>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import { LayerFeatureType, type LayerFeatureDetail, type LayerFeatureSpec } from "@/model/DataModel"

const props = defineProps<{
	details: LayerFeatureDetail[]
}>()

const emit = defineEmits<{
	(e: "collapse"): void
	(e: "close"): void
}>()

const { t, locale } = useI18n()
const expandedCityPvMinutesIds = ref(new Set<string>())

const cityPvRowOrder = [
	"date",
	"document",
	"lot-number",
	"address",
	"resolution-category",
	"resolution-type",
	"keyword-classes",
	"full-minutes",
	"resolution-or-file",
	"document-id",
	"file-id",
	"segment-id",
] as const

const CITY_PV_MINUTES_PREVIEW_LENGTH = 300
const CITY_PV_MINUTES_PREVIEW_LINES = 10

const typeOrder: LayerFeatureType[] = [
	LayerFeatureType.CityPvDocument,
	LayerFeatureType.CompletedAssessment,
	LayerFeatureType.Tod,
	LayerFeatureType.Ppu,
	LayerFeatureType.Corridor,
	LayerFeatureType.Strategic,
	LayerFeatureType.Soil,
	LayerFeatureType.TransitLine,
	LayerFeatureType.TransitStation,
	LayerFeatureType.EndangeredSpecies,
	LayerFeatureType.Zoning,
]

const grouped = computed(() => {
	const byType: Record<string, LayerFeatureDetail[]> = {}
	for (const d of props.details) {
		if (!byType[d.type]) byType[d.type] = []
		byType[d.type].push(d)
	}
	return byType
})

const orderedSections = computed(() =>
	typeOrder
		.filter(t => grouped.value[t]?.length)
		.map(t => ({
			type: t,
			items: orderedSectionItems(t),
		}))
)

function orderedSectionItems(type: LayerFeatureType) {
	const items = grouped.value[type] ?? []
	if (type !== LayerFeatureType.CityPvDocument) return items

	return [...items].sort((first, second) => cityPvDate(second).localeCompare(cityPvDate(first)))
}

function cityPvDate(item: LayerFeatureDetail) {
	const date = item.rows.find(row => row.label === "date")?.value ?? ""
	return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : ""
}

const defaultOpen = computed(() => orderedSections.value.map(s => s.type))
const accordionKey = computed(() => props.details.map(d => `${d.type}-${d.id}`).join("|"))
const persistenceKey = computed(() => `layer-details-${accordionKey.value}`)

function typeTitle(type: LayerFeatureType) {
	switch (type) {
		case LayerFeatureType.CompletedAssessment:
			return t("layer-details.types.completed-assessment")
		case LayerFeatureType.Tod:
			return t("layer-details.types.tod")
		case LayerFeatureType.Ppu:
			return t("layer-details.types.ppu")
		case LayerFeatureType.Corridor:
			return t("layer-details.types.corridor")
		case LayerFeatureType.Strategic:
			return t("layer-details.types.strategic")
		case LayerFeatureType.Soil:
			return t("layer-details.types.soil")
		case LayerFeatureType.TransitLine:
			return t("layer-details.types.transit-lines")
		case LayerFeatureType.TransitStation:
			return t("layer-details.types.transit-stations")
		case LayerFeatureType.EndangeredSpecies:
			return t("layer-details.types.endangered-species")
		case LayerFeatureType.Zoning:
			return t("layer-details.types.zoning")
		case LayerFeatureType.CityPvDocument:
			return t("layer-details.types.city-pv-documents")
		default:
			return type
	}
}

function fieldLabel(key: string) {
	return t(`layer-details.fields.${key}`, key)
}

// fields shown as a colored badge in the card header, alongside the source/vulnerability badge
const BADGE_LABELS = ["version", "species-type"]
// fields already surfaced through the header badge (name/source/vulnerability) — excluded from the detail list
const HEADER_LABELS = ["source", "name", "name-fr", "name-en", "species-vulnerability-class"]

function linkSpecs(item: LayerFeatureDetail) {
	return item.rows.filter(r => r.isLink && r.value && r.value !== "None")
}

function hasDetailRows(item: LayerFeatureDetail) {
	return item.type === LayerFeatureType.CityPvDocument
		? cityPvRows(item).length > 0
		: detailRows(item).length > 0 || linkSpecs(item).length > 0
}

function cityPvRows(item: LayerFeatureDetail) {
	const rowsByLabel = new Map(item.rows.map(row => [row.label, row]))
	return cityPvRowOrder.flatMap(label => {
		const row = rowsByLabel.get(label)
		if (!row?.value || row.value === "None") return []
		return [
			{
				label,
				value: cityPvRowValue(label, row.value),
				isLink: row.isLink === true,
			},
		]
	})
}

function cityPvRowValue(label: (typeof cityPvRowOrder)[number], value: string) {
	if (label === "date") return formatLayerDate(value)
	if (label === "keyword-classes") {
		return value
			.split(";")
			.map(keyword => keyword.trim())
			.filter(Boolean)
			.map(keyword => keyword.charAt(0).toUpperCase() + keyword.slice(1))
			.join(", ")
	}
	if (label !== "lot-number") return value

	try {
		const lotNumbers: unknown = JSON.parse(value)
		if (!Array.isArray(lotNumbers)) return value
		const formattedLotNumbers = lotNumbers
			.map(String)
			.map(lotNumber => lotNumber.trim())
			.filter(Boolean)
		return formattedLotNumbers.join(", ") || "—"
	} catch {
		return value
	}
}

function shouldCollapseCityPvMinutes(value: string) {
	return value.length > CITY_PV_MINUTES_PREVIEW_LENGTH || value.split("\n").length > CITY_PV_MINUTES_PREVIEW_LINES
}

function isCityPvMinutesExpanded(itemId: string) {
	return expandedCityPvMinutesIds.value.has(itemId)
}

function toggleCityPvMinutes(itemId: string) {
	const next = new Set(expandedCityPvMinutesIds.value)
	if (next.has(itemId)) next.delete(itemId)
	else next.add(itemId)
	expandedCityPvMinutesIds.value = next
}

function badgeRows(item: LayerFeatureDetail) {
	return item.rows
		.filter(r => BADGE_LABELS.includes(r.label) && r.value && r.value !== "None")
		.map(r => ({
			label: r.label,
			text: badgeText(r),
			badgeClass: badgeClass(r),
		}))
}

function badgeText(row: LayerFeatureSpec) {
	if (row.label === "species-type") {
		return t(`layer-details.values.species-type.${row.value}`, row.value ?? "")
	}
	return row.value ?? ""
}

function badgeClass(row: LayerFeatureSpec) {
	if (row.label === "version") {
		if (row.value === "En vigueur") return "version-green"
		if (row.value === "Projet/Adopté") return "version-amber"
	}
	if (row.label === "species-type") {
		return row.value === "Animaux" ? "species-type-badge-animal" : "species-type-badge-plant"
	}
	return "spec-neutral"
}

function detailRows(item: LayerFeatureDetail) {
	return item.rows
		.filter(
			r =>
				!r.isLink &&
				!HEADER_LABELS.includes(r.label) &&
				!BADGE_LABELS.includes(r.label) &&
				r.value &&
				r.value !== "None"
		)
		.map(r => ({
			label: r.label === "min-units-ha" ? "min-units-ha-label" : r.label,
			text: detailValueText(r),
			dotClass: detailDotClass(r),
		}))
}

function detailValueText(row: LayerFeatureSpec) {
	if (row.label === "min-units-ha") {
		return `${row.value ?? "—"} ${fieldLabel("min-units-ha")}`.trim()
	}
	if (row.label === "last-observation-date") {
		return formatLayerDate(row.value)
	}
	if (row.label === "status") {
		return t(`layer-details.values.status.${row.value}`, row.value ?? "")
	}
	if (row.label === "mode") {
		return t(`layer-details.values.mode.${row.value}`, row.value ?? "")
	}
	return row.value ?? "—"
}

function detailDotClass(row: LayerFeatureSpec) {
	if (row.label === "transit-line" || row.label === "line") {
		return transitClass(row.value)
	}
	if (row.label === "mode") {
		return transitModeClass(row.value)
	}
	return ""
}

function transitClass(val: string | null) {
	if (!val) return ""
	switch (val) {
		case "1-Verte":
			return "transit-green"
		case "2-Orange":
			return "transit-orange"
		case "4-Jaune":
			return "transit-yellow"
		case "5-Bleue":
			return "transit-blue"
		default:
			return ""
	}
}

function transitModeClass(val: string | null) {
	if (!val) return ""
	switch (val) {
		case "rem":
			return "transit-mode-rem"
		case "tram":
			return "transit-mode-tram"
		case "srb":
			return "transit-mode-srb"
		case "exo":
			return "transit-mode-exo"
		default:
			return ""
	}
}

function sourceValue(item: LayerFeatureDetail) {
	if (item.type === LayerFeatureType.EndangeredSpecies) {
		const value = item.rows.find(r => r.label === "species-vulnerability-class")?.value ?? null
		return speciesVulnerabilityLabel(value)
	}
	return item.rows.find(r => r.label === "source")?.value ?? null
}

function sourceClass(item: LayerFeatureDetail) {
	if (item.type === LayerFeatureType.EndangeredSpecies) {
		const value = item.rows.find(r => r.label === "species-vulnerability-class")?.value ?? null
		return speciesVulnerabilityClass(value)
	}
	if (planningPolicyToken(item.type)) return "planning-policy"
	return ""
}

function planningPolicyToken(type: LayerFeatureType) {
	switch (type) {
		case LayerFeatureType.Tod:
			return "--accent-planning-policy-tod"
		case LayerFeatureType.Ppu:
			return "--accent-planning-policy-ppu"
		case LayerFeatureType.Corridor:
			return "--accent-planning-policy-corridor"
		case LayerFeatureType.Strategic:
			return "--accent-planning-policy-strategic"
		default:
			return null
	}
}

function sourceStyle(item: LayerFeatureDetail) {
	const planningToken = planningPolicyToken(item.type)
	if (!planningToken) return undefined
	const badgeBg = `color-mix(in srgb, var(${planningToken}) 18%, var(--neutral-0))`
	return {
		"--badge-bg": badgeBg,
		"--badge-text": `hsl(from var(${planningToken}) h s calc(l * 0.35))`,
		"--badge-border": badgeBg,
	}
}

function nameValue(item: LayerFeatureDetail) {
	if (item.type === LayerFeatureType.EndangeredSpecies) {
		const nameFr = item.rows.find(r => r.label === "name-fr")?.value ?? null
		const nameEn = item.rows.find(r => r.label === "name-en")?.value ?? null
		return locale.value === "en" ? nameEn || nameFr : nameFr || nameEn
	}
	return item.rows.find(r => r.label === "name")?.value ?? null
}

function speciesVulnerabilityLabel(value: string | null) {
	if (!value) return value
	const normalized = value.trim()
	return t(`layer-details.values.species-vulnerability-class.${normalized}`, normalized)
}

function speciesVulnerabilityClass(value: string | null) {
	const normalized = value?.trim()
	switch (normalized) {
		case "Menacée":
		case "Vulnérable":
			return "endangered-species-threatened"
		case "Susceptible":
			return "endangered-species-susceptible"
		case "Candidate":
			return "endangered-species-candidate"
		case "Masquée":
			return "endangered-species-masked"
		default:
			return "spec-neutral"
	}
}

function formatLayerDate(value: string | null) {
	if (!value) return "—"
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return value
	return date.toLocaleDateString(locale.value === "en" ? "en-CA" : "fr-CA")
}

function transitLineDotClass(item: LayerFeatureDetail) {
	if (item.type !== LayerFeatureType.TransitLine) return ""
	const name = nameValue(item)
	switch (name) {
		case "Ligne Orange":
			return "line-dot-orange"
		case "Ligne Verte":
			return "line-dot-green"
		case "Ligne Bleue":
			return "line-dot-blue"
		case "Ligne Jaune":
			return "line-dot-yellow"
		default:
			return ""
	}
}
</script>

<style scoped>
.layers-details-panel {
	display: flex;
	flex-direction: column;
	height: 100%;
	background-color: var(--neutral-0);

	.top-section {
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		padding: var(--spacing-md) var(--spacing-lg) 0;
	}

	.scroll-body {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
		padding: var(--spacing-md) var(--spacing-lg);
	}

	.accordions {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		:deep(.accordion-content) {
			padding: var(--spacing-sm);
		}
	}

	.layer-cards {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
	}

	.layer-card {
		background: var(--neutral-0);
		border: var(--border-width-thin) solid var(--neutral-100);
		border-radius: var(--border-radius-lg);
		box-shadow: none;
		padding: var(--spacing-sm) var(--spacing-md);
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);

		&.city-pv-card .card-header .card-title {
			font-family: var(--font-family-primary);
			font-weight: var(--font-weight-medium);
		}

		&.city-pv-card .detail-value {
			min-width: 0;
			max-width: 65%;
			display: block;
			overflow-wrap: anywhere;
			word-break: break-word;
		}

		.card-header {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			justify-content: space-between;
			padding-top: var(--spacing-xs);
			padding-bottom: var(--spacing-xs);

			.card-title {
				display: inline-flex;
				align-items: center;
				gap: var(--space-6);
				font-size: var(--font-size-small);
				color: var(--neutral-900);
				font-weight: var(--font-weight-regular);
			}

			.card-badges {
				display: inline-flex;
				align-items: center;
				gap: var(--spacing-xxs);
				flex-shrink: 0;
			}
		}

		.badge {
			display: inline-flex;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background: var(--badge-bg, var(--neutral-200));
			color: var(--badge-text, var(--neutral-900));
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			border: var(--border-width-thin) solid var(--badge-border, var(--neutral-50));
		}

		.card-header:has(.card-badges) + .detail-rows {
			margin-top: var(--spacing-xxs);
			padding-top: var(--spacing-xs);
			border-top: var(--border-width-thin) solid var(--neutral-100);
		}

		.detail-rows {
			display: flex;
			flex-direction: column;
			padding-bottom: var(--spacing-xxs);
		}

		.detail-row {
			display: flex;
			align-items: baseline;
			justify-content: space-between;
			gap: var(--spacing-sm);
			padding: var(--spacing-sm) 0 var(--spacing-xs);
			border-bottom: var(--border-width-thin) solid var(--neutral-100);

			&:last-child {
				border-bottom: none;
			}

			.detail-label {
				flex: 0 0 auto;
				font-size: var(--font-size-xs);
				color: var(--neutral-600);
			}

			.detail-value {
				display: inline-flex;
				align-items: center;
				gap: var(--space-6);
				font-size: var(--font-size-xs);
				color: var(--neutral-900);
				text-align: right;
			}

			&.detail-row-stacked {
				align-items: stretch;
				flex-direction: column;

				.minutes-value {
					display: flex;
					align-items: flex-start;
					flex-direction: column;
					gap: var(--spacing-xs);
					width: 100%;

					p {
						display: -webkit-box;
						margin: 0;
						overflow: hidden;
						color: var(--neutral-900);
						font-size: var(--font-size-xs);
						line-height: var(--line-height-normal);
						white-space: pre-wrap;
						-webkit-box-orient: vertical;
						-webkit-line-clamp: 10;

						&.is-expanded {
							display: block;
							overflow: visible;
							-webkit-line-clamp: unset;
						}
					}

					.minutes-toggle {
						padding: 0;
						border: none;
						background: none;
						color: var(--primary-green);
						cursor: pointer;
						font: inherit;
						font-size: var(--font-size-xs);
						font-weight: var(--font-weight-semibold);

						&:hover {
							text-decoration: underline;
						}
					}
				}
			}
		}
	}

	.link-pill {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xxs);
		padding: var(--spacing-xxs) var(--spacing-sm);
		border-radius: var(--border-radius-full);
		background-color: var(--neutral-100);
		color: var(--neutral-700);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		text-decoration: none;
		transition:
			background-color 0.2s ease,
			color 0.2s ease;

		&:hover {
			background-color: var(--neutral-200);
			color: var(--neutral-900);
		}
	}

	.badge.version-green {
		background: color-mix(in srgb, var(--primary-green) 18%, var(--neutral-0));
		color: hsl(from var(--primary-green) h s calc(l * 0.5));
		border-color: transparent;
	}

	.badge.version-amber {
		background: color-mix(in srgb, var(--accent-transit-line-orange) 18%, var(--neutral-0));
		color: hsl(from var(--accent-transit-line-orange) h s calc(l * 0.5));
		border-color: transparent;
	}

	.badge.species-type-badge-animal {
		background: var(--accent-species-type-animal-bg);
		color: var(--accent-species-type-animal-text);
		border-color: transparent;
	}

	.badge.species-type-badge-plant {
		background: var(--accent-species-type-plant-bg);
		color: var(--accent-species-type-plant-text);
		border-color: transparent;
	}

	.badge.endangered-species-threatened {
		--badge-bg: color-mix(in srgb, var(--accent-endangered-species-threatened) 18%, var(--neutral-0));
		--badge-text: hsl(from var(--accent-endangered-species-threatened) h s calc(l * 0.35));
		--badge-border: var(--badge-bg);
	}

	.badge.endangered-species-susceptible {
		--badge-bg: color-mix(in srgb, var(--accent-endangered-species-susceptible) 18%, var(--neutral-0));
		--badge-text: hsl(from var(--accent-endangered-species-susceptible) h s calc(l * 0.35));
		--badge-border: var(--badge-bg);
	}

	.badge.endangered-species-candidate {
		--badge-bg: color-mix(in srgb, var(--accent-endangered-species-candidate) 18%, var(--neutral-0));
		--badge-text: hsl(from var(--accent-endangered-species-candidate) h s calc(l * 0.35));
		--badge-border: var(--badge-bg);
	}

	.badge.endangered-species-masked {
		--badge-bg: color-mix(in srgb, var(--accent-endangered-species-masked) 18%, var(--neutral-0));
		--badge-text: hsl(from var(--accent-endangered-species-masked) h s calc(l * 0.35));
		--badge-border: var(--badge-bg);
	}

	.value-dot.transit-green {
		background: var(--accent-transit-line-green);
	}

	.value-dot.transit-orange {
		background: var(--accent-transit-line-orange);
	}

	.value-dot.transit-yellow {
		background: var(--accent-transit-line-yellow);
	}

	.value-dot.transit-blue {
		background: var(--accent-transit-line-blue);
	}

	.value-dot.transit-mode-rem {
		background: var(--accent-transit-mode-rem-bg);
	}

	.value-dot.transit-mode-tram {
		background: var(--accent-transit-mode-tram-bg);
	}

	.value-dot.transit-mode-srb {
		background: var(--accent-transit-mode-srb-bg);
	}

	.value-dot.transit-mode-exo {
		background: var(--accent-transit-mode-exo-bg);
	}

	.line-dot,
	.value-dot {
		display: inline-block;
		width: var(--width-line-dot);
		height: var(--height-line-dot);
		border-radius: 999px;
		flex-shrink: 0;
	}

	.line-dot-green {
		background: var(--accent-transit-line-green);
	}

	.line-dot-orange {
		background: var(--accent-transit-line-orange);
	}

	.line-dot-yellow {
		background: var(--accent-transit-line-yellow);
	}

	.line-dot-blue {
		background: var(--accent-transit-line-blue);
	}

	.header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: var(--spacing-sm);

		.header-actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			flex-shrink: 0;
		}
	}

	.separator {
		width: 100%;
		height: var(--border-width-thin);
		flex-shrink: 0;
		background-color: var(--neutral-400);
	}

	.icon-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		padding: 0;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-full);
		background: var(--neutral-0);
		color: var(--neutral-600);
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			color: var(--primary-green);
			border-color: var(--primary-green);
		}

		&.collapse-btn {
			width: auto;
			height: auto;
			padding: var(--spacing-sm);
			border: none;
			border-radius: var(--border-radius-sm);
			background: none;

			&:hover {
				background: var(--neutral-100);
				color: var(--neutral-900);
			}
		}
	}

	.title {
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-medium);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
		margin: 0;
	}
}
</style>
